import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './Dashboard.css';

const API_URL = process.env.REACT_APP_API_URL || '';
const getToken = () => localStorage.getItem('notes_token') || '';
const apiFetch = (url, opts = {}) => {
  const token = getToken();
  if (token && !opts.headers) opts.headers = {};
  if (token) opts.headers['x-token'] = token;
  return fetch(url, opts);
};

export default function PatchManager() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState('list'); // list | form | parse-email
  
  // 表单数据
  const emptyForm = {
    project: '',
    patchName: '',
    patchDesc: '',
    needDeploy: '否',
    hasMetadata: '否',
    hasScript: '否',
    tester: '',
    releaseDate: new Date().toISOString().split('T')[0]
  };
  const [formData, setFormData] = useState({ ...emptyForm });
  
  // 新增模式待上传附件
  const [newAttachments, setNewAttachments] = useState([]);
  
  // 筛选条件
  const [filter, setFilter] = useState({
    project: '',
    projectSearch: '',
    showProjectDrop: false,
    startDate: '',
    endDate: ''
  });
  
  // 编辑状态
  const [editingId, setEditingId] = useState(null);
  
  // 展开查看附件的行
  const [expandedId, setExpandedId] = useState(null);
  
  // 项目搜索
  const [projectSearch, setProjectSearch] = useState('');
  const [showProjectDropdown, setShowProjectDropdown] = useState(false);

  // 邮件解析状态
  const [emailFile, setEmailFile] = useState(null);
  const [emailFiles, setEmailFiles] = useState([]); // 支持多文件
  const [emailProject, setEmailProject] = useState('');
  const [emailProjectSearch, setEmailProjectSearch] = useState('');
  const [emailParsing, setEmailParsing] = useState(false);
  const [emailResult, setEmailResult] = useState(null);
  const [emailProjectDropShow, setEmailProjectDropShow] = useState(false);

  // 获取项目列表
  useEffect(() => {
    apiFetch(`${API_URL}/api/workorder/projects`)
      .then(r => r.json())
      .then(data => {
        if (data.success) setProjects(data.projects || []);
      })
      .catch(console.error);
  }, []);

  // 加载记录
  const loadRecords = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filter.project) params.append('project', filter.project);
      if (filter.startDate) params.append('startDate', filter.startDate);
      if (filter.endDate) params.append('endDate', filter.endDate);
      const res = await apiFetch(`${API_URL}/api/patch/list?${params}`);
      const data = await res.json();
      if (data.success) setRecords(data.data || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => { loadRecords(); }, [filter]);

  // 提交表单
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.project || !formData.patchName || !formData.releaseDate) {
      alert('项目、补丁名称、发布日期为必填项');
      return;
    }
    try {
      // 新增模式：先创建记录
      if (!editingId) {
        const res = await apiFetch(`${API_URL}/api/patch/create`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(formData)
        });
        const data = await res.json();
        if (data.success && data.id) {
          // 上传附件
          if (newAttachments.length > 0) {
            for (const file of newAttachments) {
              const fd = new FormData();
              fd.append('file', file);
              await apiFetch(`${API_URL}/api/patch/${data.id}/attachment`, { method: 'POST', body: fd });
            }
          }
          alert('添加成功');
          setNewAttachments([]);
          goBack();
          loadRecords();
        } else {
          alert(data.error || '操作失败');
        }
      } else {
        // 编辑模式
        const res = await apiFetch(`${API_URL}/api/patch/${editingId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(formData)
        });
        const data = await res.json();
        if (data.success) {
          alert('更新成功');
          goBack();
          loadRecords();
        } else {
          alert(data.error || '操作失败');
        }
      }
    } catch (e) { alert('网络错误'); }
  };

  const goBack = () => {
    setPage('list');
    setEditingId(null);
    setFormData({ ...emptyForm });
    setProjectSearch('');
    setNewAttachments([]);
  };

  // 邮件解析处理
  const handleEmailParse = async () => {
    const filesToParse = emailFiles.length > 0 ? emailFiles : (emailFile ? [emailFile] : []);
    if (filesToParse.length === 0) {
      alert('请选择eml文件');
      return;
    }
    if (!emailProject) {
      alert('请选择项目');
      return;
    }
    setEmailParsing(true);
    setEmailResult(null);
    try {
      const fd = new FormData();
      filesToParse.forEach(file => fd.append('emlFiles', file));
      fd.append('project', emailProject);
      const token = localStorage.getItem('notes_token') || '';
      const res = await apiFetch(`${API_URL}/api/patch/parse-emails`, {
        method: 'POST',
        headers: { 'x-token': token },
        body: fd
      });
      const data = await res.json();
      if (data.success) {
        const successCount = data.results.filter(r => r.success).length;
        const failCount = data.results.filter(r => !r.success).length;
        setEmailResult({ count: successCount, results: data.results });
        alert(`批量解析完成！\n成功: ${successCount} 条\n${failCount > 0 ? '失败: ' + failCount + ' 条' : ''}`);
        loadRecords();
      } else {
        alert(data.error || '解析失败');
      }
    } catch (e) {
      alert('网络错误');
    }
    setEmailParsing(false);
  };

  // 新增
  const handleNew = () => {
    setEditingId(null);
    setFormData({ ...emptyForm });
    setProjectSearch('');
    setNewAttachments([]);
    setPage('form');
  };

  // 编辑
  const handleEdit = (record) => {
    setFormData({
      project: record.project,
      patchName: record.patchName,
      patchDesc: record.patchDesc || record.problemDesc || '',
      needDeploy: record.needDeploy || '否',
      hasMetadata: record.hasMetadata || '否',
      hasScript: record.hasScript || '否',
      tester: record.tester || '',
      releaseDate: record.releaseDate || record.updateDate || ''
    });
    setEditingId(record.id);
    setProjectSearch(record.project);
    setPage('form');
  };

  // 删除
  const handleDelete = async (id) => {
    if (!window.confirm('确定删除该记录及其附件？')) return;
    try {
      const res = await apiFetch(`${API_URL}/api/patch/${id}`, { method: 'DELETE' });
      const data = await res.json();
      if (data.success) loadRecords();
      else alert(data.error);
    } catch (e) { alert('删除失败'); }
  };

  // 上传附件
  const handleUpload = async (id) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.multiple = true;
    input.onchange = async (e) => {
      for (const file of e.target.files) {
        const fd = new FormData();
        fd.append('file', file);
        try {
          await apiFetch(`${API_URL}/api/patch/${id}/attachment`, { method: 'POST', body: fd });
        } catch (err) { console.error(err); }
      }
      loadRecords();
      alert('上传完成');
    };
    input.click();
  };

  // 下载附件
  const handleDownload = (id, filename, originalName) => {
    const a = document.createElement('a');
    a.href = `${API_URL}/api/patch/${id}/attachment/${filename}`;
    a.download = originalName || filename;
    a.click();
  };

  // 删除附件
  const handleDeleteAttachment = async (id, filename) => {
    if (!window.confirm('确定删除该附件？')) return;
    try {
      await apiFetch(`${API_URL}/api/patch/${id}/attachment/${filename}`, { method: 'DELETE' });
      loadRecords();
    } catch (e) { alert('删除失败'); }
  };

  // 导出Excel
  const handleExport = () => {
    const params = new URLSearchParams();
    if (filter.project) params.append('project', filter.project);
    if (filter.startDate) params.append('startDate', filter.startDate);
    if (filter.endDate) params.append('endDate', filter.endDate);
    window.open(`${API_URL}/api/patch/export?${params}`);
  };

  const filteredProjects = projects.filter(p =>
    p.toLowerCase().includes(projectSearch.toLowerCase())
  );

  // ===== 录入页面 =====
  if (page === 'form') {
    return (
      <div className="patch-manager">
        <div className="patch-header">
          <button onClick={goBack} className="btn-back">← 返回列表</button>
          <h2>{editingId ? '编辑补丁记录' : '新建补丁记录'}</h2>
        </div>

        <div className="patch-form-card">
          <form onSubmit={handleSubmit}>
            <div className="patch-form-grid">
              {/* 项目名称 */}
              <div className="form-group">
                <label>项目名称 *</label>
                <div className="project-select-wrapper">
                  <input
                    type="text"
                    value={projectSearch || formData.project}
                    onChange={(e) => {
                      setProjectSearch(e.target.value);
                      setFormData({ ...formData, project: e.target.value });
                      setShowProjectDropdown(true);
                    }}
                    onFocus={() => setShowProjectDropdown(true)}
                    placeholder="输入或选择项目..."
                    className="form-input"
                  />
                  {showProjectDropdown && (
                    <div className="project-dropdown">
                      {filteredProjects.length > 0
                        ? filteredProjects.slice(0, 10).map(p => (
                            <div key={p} className="project-option" onClick={() => {
                              setFormData({ ...formData, project: p });
                              setProjectSearch(p);
                              setShowProjectDropdown(false);
                            }}>{p}</div>
                          ))
                        : <div className="project-empty">无匹配项目</div>}
                    </div>
                  )}
                </div>
              </div>

              {/* 补丁名称 */}
              <div className="form-group">
                <label>补丁名称 *</label>
                <input
                  type="text"
                  value={formData.patchName}
                  onChange={(e) => setFormData({ ...formData, patchName: e.target.value })}
                  className="form-input"
                  placeholder="如: NCC_补丁_20260401"
                />
              </div>

              {/* 补丁描述 */}
              <div className="form-group full-width">
                <label>补丁描述</label>
                <textarea
                  value={formData.patchDesc}
                  onChange={(e) => setFormData({ ...formData, patchDesc: e.target.value })}
                  className="form-textarea"
                  rows={3}
                  placeholder="描述补丁解决的问题..."
                />
              </div>

              {/* 是否需要部署 */}
              <div className="form-group">
                <label>是否需要部署</label>
                <select value={formData.needDeploy} onChange={(e) => setFormData({ ...formData, needDeploy: e.target.value })} className="form-select">
                  <option value="否">否</option>
                  <option value="是">是</option>
                </select>
              </div>

              {/* 是否有元数据 */}
              <div className="form-group">
                <label>是否有元数据</label>
                <select value={formData.hasMetadata} onChange={(e) => setFormData({ ...formData, hasMetadata: e.target.value })} className="form-select">
                  <option value="否">否</option>
                  <option value="是">是</option>
                </select>
              </div>

              {/* 是否有脚本 */}
              <div className="form-group">
                <label>是否有脚本</label>
                <select value={formData.hasScript} onChange={(e) => setFormData({ ...formData, hasScript: e.target.value })} className="form-select">
                  <option value="否">否</option>
                  <option value="是">是</option>
                </select>
              </div>

              {/* 测试人 */}
              <div className="form-group">
                <label>测试人</label>
                <input
                  type="text"
                  value={formData.tester}
                  onChange={(e) => setFormData({ ...formData, tester: e.target.value })}
                  className="form-input"
                  placeholder="测试人员姓名"
                />
              </div>

              {/* 发布日期 */}
              <div className="form-group">
                <label>发布日期 *</label>
                <input
                  type="date"
                  value={formData.releaseDate}
                  onChange={(e) => setFormData({ ...formData, releaseDate: e.target.value })}
                  className="form-input"
                />
              </div>
            </div>

            {/* 附件上传区域 */}
            <div className="form-attachments">
              <div className="attachment-header">
                <span>📎 附件</span>
                <button type="button" onClick={() => {
                  if (editingId) {
                    handleUpload(editingId);
                  } else {
                    const input = document.createElement('input');
                    input.type = 'file';
                    input.multiple = true;
                    input.onchange = (e) => {
                      setNewAttachments([...newAttachments, ...Array.from(e.target.files)]);
                    }; 
                    input.click();
                  }
                }} className="btn-add-att">+ 上传附件</button>
              </div>
              
              {/* 编辑模式：显示已有附件 */}
              {editingId && (() => {
                const record = records.find(r => r.id === editingId);
                const atts = record && record.attachments || [];
                return atts.length > 0 ? (
                  <div className="attachment-list">
                    {atts.map((att, i) => (
                      <div key={i} className="attachment-item">
                        <span className="att-icon">📄</span>
                        <span className="att-name" onClick={() => handleDownload(editingId, att.filename, att.originalName)}>{att.originalName}</span>
                        <span className="att-size">{(att.size / 1024).toFixed(1)}KB</span>
                        <span className="att-delete" onClick={() => handleDeleteAttachment(editingId, att.filename)}>✕</span>
                      </div>
                    ))}
                  </div>
                ) : <p className="no-attachment">暂无附件</p>;
              })()}
              
              {/* 新增模式：显示待上传附件 */}
              {!editingId && newAttachments.length > 0 && (
                <div className="attachment-list">
                  {newAttachments.map((file, i) => (
                    <div key={i} className="attachment-item">
                      <span className="att-icon">📄</span>
                      <span className="att-name">{file.name}</span>
                      <span className="att-size">{(file.size / 1024).toFixed(1)}KB</span>
                      <span className="att-delete" onClick={() => setNewAttachments(newAttachments.filter((_, idx) => idx !== i))}>✕</span>
                    </div>
                  ))}
                </div>
              )}
              {!editingId && newAttachments.length === 0 && <p className="no-attachment">暂无附件</p>}
            </div>

            <div className="form-actions">
              <button type="submit" className="btn-primary">{editingId ? '更新' : '提交'}</button>
              <button type="button" onClick={goBack} className="btn-secondary">取消</button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  // ===== 邮件解析页面 =====
  if (page === 'parse-email') {
    const filteredEmailProjects = projects.filter(p =>
      p.toLowerCase().includes((emailProjectSearch || emailProject || '').toLowerCase())
    );
    return (
      <div className="patch-manager">
        <div className="patch-header">
          <button onClick={() => setPage('list')} className="btn-back">← 返回列表</button>
          <h2>📧 邮件解析</h2>
        </div>

        <div className="email-parse-card">
          {/* 步骤指引 */}
          <div className="email-parse-steps">
            <div className={`step ${emailFile ? 'done' : 'active'}`}>
              <div className="step-num">1</div>
              <div className="step-text">上传邮件</div>
            </div>
            <div className="step-line"></div>
            <div className={`step ${emailFile && emailProject ? 'active' : emailFile ? 'active' : ''} ${emailProject ? 'done' : ''}`}>
              <div className="step-num">2</div>
              <div className="step-text">选择项目</div>
            </div>
            <div className="step-line"></div>
            <div className={`step ${emailResult ? 'done' : ''}`}>
              <div className="step-num">3</div>
              <div className="step-text">完成解析</div>
            </div>
          </div>

          {/* 上传区域 */}
          <div className="email-upload-zone" onClick={() => document.getElementById('emlFileInput').click()}>
            {emailFiles.length > 0 ? (
              <div className="files-list">
                <div className="files-header">
                  <span>已选择 {emailFiles.length} 个邮件文件</span>
                  <button className="btn-clear-files" onClick={(e) => { e.stopPropagation(); setEmailFiles([]); }}>清空</button>
                </div>
                {emailFiles.map((file, idx) => (
                  <div key={idx} className="file-item">
                    <span className="file-icon-small">📧</span>
                    <span className="file-name-small">{file.name}</span>
                    <span className="file-size-small">{(file.size / 1024).toFixed(1)}KB</span>
                    <button className="btn-remove-file" onClick={(e) => { e.stopPropagation(); setEmailFiles(emailFiles.filter((_, i) => i !== idx)); }}>✕</button>
                  </div>
                ))}
              </div>
            ) : emailFile ? (
              <div className="file-selected">
                <div className="file-icon">📧</div>
                <div className="file-info">
                  <div className="file-name">{emailFile.name}</div>
                  <div className="file-size">{(emailFile.size / 1024).toFixed(1)} KB</div>
                </div>
                <div className="file-status">✓ 已选择</div>
              </div>
            ) : (
              <>
                <div className="upload-icon">📤</div>
                <div className="upload-text">点击上传 .eml 邮件文件</div>
                <div className="upload-hint">支持多选，Outlook、Foxmail 等导出的 eml 格式</div>
              </>
            )}
          </div>
          <input
            id="emlFileInput"
            type="file"
            accept=".eml"
            multiple
            style={{ display: 'none' }}
            onChange={(e) => { 
              const files = Array.from(e.target.files);
              setEmailFiles(files);
              setEmailFile(files.length === 1 ? files[0] : null);
            }}
          />

          {/* 选择项目 */}
          <div className="form-group" style={{ marginTop: 20 }}>
            <label>所属项目</label>
            <div className="project-select-wrapper">
              <input
                type="text"
                value={emailProjectSearch || emailProject}
                onChange={(e) => { setEmailProjectSearch(e.target.value); setEmailProject(e.target.value); setEmailProjectDropShow(true); }}
                onFocus={() => { setEmailProjectDropShow(true); if (!emailProjectSearch) setEmailProjectSearch(''); }}
                placeholder="输入或选择项目..."
                className="form-input"
              />
              {emailProjectDropShow && filteredEmailProjects.length > 0 && (
                <div className="project-dropdown show">
                  {filteredEmailProjects.slice(0, 8).map(p => (
                    <div key={p} className="project-option" onClick={() => {
                      setEmailProject(p);
                      setEmailProjectSearch(p);
                      setEmailProjectDropShow(false);
                    }}>{p}</div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* 解析结果 */}
          {emailResult && emailResult.results ? (
            <div className="parse-result-card">
              <div className="result-header"><span className="result-icon">✅</span> 批量解析完成，已创建 {emailResult.count} 条补丁记录</div>
              <div className="result-list">
                {emailResult.results.map((r, idx) => (
                  <div key={idx} className={`result-item-batch ${r.success ? 'success' : 'error'}`}>
                    <span className="result-file">{r.filename}</span>
                    {r.success ? (
                      <span className="result-patch">→ {r.patchName}</span>
                    ) : (
                      <span className="result-error">{r.error}</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ) : emailResult && emailResult.subject ? (
            <div className="parse-result-card">
              <div className="result-header"><span className="result-icon">✅</span> 解析完成，已创建补丁记录</div>
              <div className="result-grid">
                <div className="result-item"><span className="result-label">邮件主题</span><span className="result-value">{emailResult.subject}</span></div>
                <div className="result-item"><span className="result-label">补丁名称</span><span className="result-value">{emailResult.patchName}</span></div>
                <div className="result-item"><span className="result-label">补丁描述</span><span className="result-value">{emailResult.patchDesc}</span></div>
                <div className="result-item"><span className="result-label">发布日期</span><span className="result-value">{emailResult.date}</span></div>
              </div>
            </div>
          ) : null}

          {/* 操作按钮 */}
          <div className="email-parse-actions">
            <button
              onClick={handleEmailParse}
              className="btn-primary btn-parse"
              disabled={emailParsing || (emailFiles.length === 0 && !emailFile) || !emailProject}
            >
              {emailParsing ? (
                <><span className="spinner-small"></span> 解析中 ({emailFiles.length || 1} 封)...</>
              ) : (
                <>🔍 解析邮件并创建记录{emailFiles.length > 1 ? ` (${emailFiles.length}封)` : ''}</>
              )}
            </button>
            <button onClick={() => setPage('list')} className="btn-secondary">取消</button>
          </div>
        </div>
      </div>
    );
  }

  // ===== 查询/列表页面（默认） =====
  return (
    <div className="patch-manager">
      <div className="patch-header">
        <button onClick={() => navigate('/')} className="btn-back">← 返回首页</button>
        <h2>🔧 补丁记录管理</h2>
        <button onClick={handleNew} className="btn-primary">+ 新增记录</button>
        <button onClick={() => { setPage('parse-email'); setEmailFile(null); setEmailFiles([]); setEmailProject(''); setEmailProjectSearch(''); setEmailResult(null); }} className="btn-email-parse">📧 邮件解析</button>
      </div>

      {/* 筛选和导出 */}
      <div className="patch-filter-card">
        <div className="filter-row">
          <div className="filter-item project-filter-item">
            <label>项目</label>
            <div className="project-select-wrapper">
              <input
                type="text"
                value={filter.projectSearch || filter.project}
                onChange={(e) => setFilter({ ...filter, projectSearch: e.target.value, project: e.target.value, showProjectDrop: true })}
                onFocus={() => setFilter({ ...filter, showProjectDrop: true })}
                placeholder="输入或选择项目..."
                className="form-input"
              />
              {filter.showProjectDrop && (
                <div className="project-dropdown">
                  {projects.filter(p => p.toLowerCase().includes((filter.projectSearch || '').toLowerCase())).slice(0, 10).map(p => (
                    <div key={p} className="project-option" onClick={() => setFilter({ ...filter, project: p, projectSearch: p, showProjectDrop: false })}>{p}</div>
                  ))}
                  {projects.filter(p => p.toLowerCase().includes((filter.projectSearch || '').toLowerCase())).length === 0 && <div className="project-empty">无匹配项目</div>}
                </div>
              )}
            </div>
          </div>
          <div className="filter-item">
            <label>开始日期</label>
            <input type="date" value={filter.startDate} onChange={(e) => setFilter({ ...filter, startDate: e.target.value })} className="form-input" />
          </div>
          <div className="filter-item">
            <label>结束日期</label>
            <input type="date" value={filter.endDate} onChange={(e) => setFilter({ ...filter, endDate: e.target.value })} className="form-input" />
          </div>
          <button onClick={handleExport} className="btn-export">📥 导出Excel</button>
        </div>
      </div>

      {/* 记录列表 */}
      <div className="patch-list-card">
        <h3>补丁记录列表 ({records.length}条)</h3>
        {loading ? (
          <p className="loading-text">加载中...</p>
        ) : records.length === 0 ? (
          <p className="empty-text">暂无记录，点击右上角新增</p>
        ) : (
          <div className="patch-table-wrapper">
            <table className="patch-table">
              <thead>
                <tr>
                  <th>序号</th>
                  <th>项目</th>
                  <th>补丁名称</th>
                  <th>补丁描述</th>
                  <th>部署</th>
                  <th>元数据</th>
                  <th>脚本</th>
                  <th>测试人</th>
                  <th>发布日期</th>
                  <th>操作</th>
                </tr>
              </thead>
              <tbody>
                {records.map((r, idx) => (
                  <React.Fragment key={r.id}>
                    <tr className={expandedId === r.id ? 'expanded-row' : ''} onClick={() => setExpandedId(expandedId === r.id ? null : r.id)}>
                      <td>{idx + 1}</td>
                      <td>{r.project}</td>
                      <td>{r.patchName}</td>
                      <td className="desc-cell">{r.patchDesc || r.problemDesc || '-'}</td>
                      <td>{r.needDeploy}</td>
                      <td>{r.hasMetadata}</td>
                      <td>{r.hasScript}</td>
                      <td>{r.tester || '-'}</td>
                      <td>{r.releaseDate || r.updateDate}</td>
                      <td>
                        <div className="action-btns">
                          <button onClick={(e) => { e.stopPropagation(); handleEdit(r); }} className="btn-edit">编辑</button>
                          <button onClick={(e) => { e.stopPropagation(); handleUpload(r.id); }} className="btn-upload-btn">📎</button>
                          <button onClick={(e) => { e.stopPropagation(); handleDelete(r.id); }} className="btn-delete">删除</button>
                        </div>
                      </td>
                    </tr>
                    {/* 展开行 - 显示附件 */}
                    {expandedId === r.id && (
                      <tr className="attachment-row">
                        <td colSpan="9">
                          <div className="attachment-detail">
                            <div className="attachment-header">
                              <span>📎 附件 ({(r.attachments && r.attachments.length) || 0}个)</span>
                              <button onClick={() => handleUpload(r.id)} className="btn-add-att">+ 上传附件</button>
                            </div>
                            {r.attachments && r.attachments.length > 0 ? (
                              <div className="attachment-list">
                                {r.attachments.map((att, i) => (
                                  <div key={i} className="attachment-item">
                                    <span className="att-icon">📄</span>
                                    <span className="att-name" onClick={() => handleDownload(r.id, att.filename, att.originalName)}>
                                      {att.originalName}
                                    </span>
                                    <span className="att-size">{(att.size / 1024).toFixed(1)}KB</span>
                                    <span className="att-download" onClick={() => handleDownload(r.id, att.filename, att.originalName)}>下载</span>
                                    <span className="att-delete" onClick={() => handleDeleteAttachment(r.id, att.filename)}>✕</span>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <p className="no-attachment">暂无附件</p>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
